import React, { useState, useRef, useEffect } from "react";
import {
  ChevronsUpDown,
  ChevronDown,
  ChevronRight,
  Plus,
  User,
  Settings,
  LogOut,
} from "lucide-react";
import {
  useActiveOrganization,
  useSession,
  useActiveTenancy,
} from "@wacht/tanstack-router";
import {
  useOrganizationMemberships,
  useWorkspaceList,
} from "@wacht/tanstack-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenuButton, useSidebar } from "@/components/ui/sidebar";
import { CreateWorkspaceForm } from "./create-workspace-form";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface OrganizationSwitcherProps {
  showPersonal?: boolean;
}

export const OrganizationSwitcher = ({
  showPersonal = false,
}: OrganizationSwitcherProps) => {
  const { state } = useSidebar();
  const [open, setOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [selectedOrgForWorkspace, setSelectedOrgForWorkspace] = useState<
    string | null
  >(null);

  const {
    organizationMemberships,
    loading: organizationLoading,
  } = useOrganizationMemberships();
  const { activeOrganization } = useActiveOrganization();
  const { workspaceMembership } = useActiveTenancy();
  const { workspaces: workspaceList, loading: workspacesLoading } =
    useWorkspaceList();
  
  const activeWorkspace = workspaceMembership?.workspace;
  const {
    session,
    loading: sessionLoading,
    switchOrganization,
    switchWorkspace,
  } = useSession();

  const isPersonalActive =
    !session?.active_signin?.active_organization_membership_id;

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const currentDisplay = React.useMemo(() => {
    if (isPersonalActive) {
      return {
        name: "Personal account",
        image_url: session?.active_signin?.user?.profile_picture_url,
        isPersonal: true,
      };
    }

    let displayName = activeOrganization?.name || "";
    if (activeWorkspace) {
      displayName = `${activeOrganization?.name} / ${activeWorkspace.name || ""}`;
    }

    return {
      name: displayName,
      image_url: activeOrganization?.image_url,
      isPersonal: false,
    };
  }, [
    isPersonalActive,
    activeOrganization,
    activeWorkspace,
    session,
  ]);

  const handleSwitchOrganization = async (orgId?: string) => {
    setIsSwitching(true);
    try {
      await switchOrganization(orgId);
    } catch (error) {
      toast.error("Failed to switch organization");
    } finally {
      setIsSwitching(false);
    }
  };

  const handleSwitchWorkspace = async (workspaceId: string) => {
    setIsSwitching(true);
    try {
      await switchWorkspace(workspaceId);
    } catch (error) {
      toast.error("Failed to switch workspace");
    } finally {
      setIsSwitching(false);
    }
  };

  const toggleOrgExpanded = (orgId: string) => {
    const newExpanded = new Set(expandedOrgs);
    if (newExpanded.has(orgId)) {
      newExpanded.delete(orgId);
    } else {
      newExpanded.add(orgId);
    }
    setExpandedOrgs(newExpanded);
  };

  if (organizationLoading || sessionLoading) {
    const isCollapsed = state === "collapsed";
    return (
      <div
        className={cn(
          "rounded-md bg-background animate-pulse border",
          isCollapsed ? "size-8 shrink-0" : "w-full min-w-[200px] min-h-[42px]"
        )}
      />
    );
  }

  const orgWorkspaces = workspaceList?.filter(
    (w: any) => w.organization.id === activeOrganization?.id,
  ) || [];

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={currentDisplay.name}
            variant="outline"
            className="w-full justify-between group-data-[collapsible=icon]:justify-center"
            disabled={isSwitching}
          >
            <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center flex-shrink-0 group-data-[collapsible=icon]:size-6">
              {currentDisplay.image_url ? (
                <img
                  src={currentDisplay.image_url}
                  alt={currentDisplay.name}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : currentDisplay.isPersonal ? (
                <User className="w-3 h-3" />
              ) : (
                getInitials(currentDisplay.name)
              )}
            </div>
            <span className="truncate text-sm font-normal group-data-[collapsible=icon]:hidden">
              {currentDisplay.name}
            </span>
            <ChevronsUpDown className="w-4 h-4 flex-shrink-0 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[300px] max-h-[400px] overflow-y-auto">
          {/* Personal Account */}
          {showPersonal && (
            <>
              <DropdownMenuItem
                onClick={() => {
                  if (!isPersonalActive) {
                    handleSwitchOrganization();
                  }
                }}
                disabled={isSwitching || isPersonalActive}
                className={cn(
                  "flex items-center justify-between",
                  isPersonalActive && "bg-accent",
                )}
              >
                <div className="flex items-center gap-2 flex-1">
                  {isPersonalActive && (
                    <div className="w-2 h-2 rounded-full bg-primary absolute left-2" />
                  )}
                  <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                    {session?.active_signin?.user?.profile_picture_url ? (
                      <img
                        src={session.active_signin.user.profile_picture_url}
                        alt="Personal account"
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <User className="w-3 h-3 text-muted-foreground" />
                    )}
                  </div>
                  <span className="text-sm">Personal account</span>
                </div>
                {!isPersonalActive && (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
              </DropdownMenuItem>
              {!isPersonalActive && activeOrganization && (
                <DropdownMenuSeparator />
              )}
            </>
          )}

          {/* Active Organization */}
          {!isPersonalActive && activeOrganization && (
            <>
              <DropdownMenuItem
                onClick={() => {
                  if (workspaceList && workspaceList.length > 0) {
                    toggleOrgExpanded(activeOrganization.id);
                  }
                }}
                className={cn(
                  "flex items-center justify-between bg-accent",
                  isSwitching && "opacity-70 pointer-events-none",
                )}
              >
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-2 h-2 rounded-full bg-primary absolute left-2" />
                  <ChevronDown
                    className={cn(
                      "w-3 h-3 transition-transform",
                      expandedOrgs.has(activeOrganization.id)
                        ? "rotate-0"
                        : "-rotate-90",
                    )}
                  />
                  <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">
                    {activeOrganization.image_url ? (
                      <img
                        src={activeOrganization.image_url}
                        alt={activeOrganization.name}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      getInitials(activeOrganization.name)
                    )}
                  </div>
                  <span className="text-sm">{activeOrganization.name}</span>
                </div>
              </DropdownMenuItem>

              {/* Workspaces for active org */}
              {expandedOrgs.has(activeOrganization.id) && orgWorkspaces.length > 0 && (
                <>
                  {orgWorkspaces.map((workspace: any) => {
                    const isWorkspaceActive =
                      activeWorkspace?.id === workspace.id;
                    return (
                      <DropdownMenuItem
                        key={workspace.id}
                        onClick={() => {
                          if (!isWorkspaceActive) {
                            handleSwitchWorkspace(workspace.id);
                          }
                        }}
                        disabled={isSwitching || isWorkspaceActive}
                        className={cn(
                          "pl-10 flex items-center justify-between",
                          isWorkspaceActive && "bg-accent",
                        )}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {isWorkspaceActive && (
                            <div className="w-2 h-2 rounded-full bg-primary absolute left-6" />
                          )}
                          <div className="w-4 h-4 rounded-full bg-muted text-muted-foreground text-[9px] font-semibold flex items-center justify-center">
                            {workspace.image_url ? (
                              <img
                                src={workspace.image_url}
                                alt={workspace.name}
                                className="w-full h-full object-cover rounded-full"
                              />
                            ) : (
                              getInitials(workspace.name).charAt(0)
                            )}
                          </div>
                          <span className="text-xs">{workspace.name}</span>
                        </div>
                        {!isWorkspaceActive && (
                          <ChevronRight className="w-3 h-3 text-muted-foreground" />
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedOrgForWorkspace(activeOrganization.id);
                      setCreateWorkspaceOpen(true);
                      setOpen(false);
                    }}
                    disabled={isSwitching}
                    className="pl-10"
                  >
                    {/* <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full border border-dashed border-muted-foreground flex items-center justify-center">
                        <Plus className="w-3 h-3 text-muted-foreground" />
                      </div>
                      <span className="text-xs">Create workspace</span>
                    </div> */}
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuSeparator />
            </>
          )}

          {/* Other Organizations */}
          {organizationMemberships &&
            organizationMemberships
              .filter(
                (m: any) => m.organization.id !== activeOrganization?.id,
              )
              .map((membership: any) => {
                const org = membership.organization;
                const orgWorkspacesList =
                  workspaceList?.filter(
                    (w: any) => w.organization.id === org.id,
                  ) || [];
                const isExpanded = expandedOrgs.has(org.id);

                return (
                  <React.Fragment key={org.id}>
                    <DropdownMenuItem
                      onClick={() => {
                        if (orgWorkspacesList.length > 0) {
                          toggleOrgExpanded(org.id);
                        } else {
                          handleSwitchOrganization(org.id);
                        }
                      }}
                      disabled={isSwitching}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        {orgWorkspacesList.length > 0 && (
                          <ChevronDown
                            className={cn(
                              "w-3 h-3 transition-transform",
                              isExpanded ? "rotate-0" : "-rotate-90",
                            )}
                          />
                        )}
                        <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center">
                          {org.image_url ? (
                            <img
                              src={org.image_url}
                              alt={org.name}
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            getInitials(org.name)
                          )}
                        </div>
                        <span className="text-sm">{org.name}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </DropdownMenuItem>

                    {/* Workspaces for other orgs */}
                    {isExpanded && orgWorkspacesList.length > 0 && (
                      <>
                        {orgWorkspacesList.map((workspace: any) => {
                          const isWorkspaceActive =
                            activeWorkspace?.id === workspace.id;
                          return (
                            <DropdownMenuItem
                              key={workspace.id}
                              onClick={() => {
                                if (!isWorkspaceActive) {
                                  handleSwitchWorkspace(workspace.id);
                                }
                              }}
                              disabled={isSwitching || isWorkspaceActive}
                              className={cn(
                                "pl-10 flex items-center justify-between",
                                isWorkspaceActive && "bg-accent",
                              )}
                            >
                              <div className="flex items-center gap-2 flex-1">
                                {isWorkspaceActive && (
                                  <div className="w-2 h-2 rounded-full bg-primary absolute left-8" />
                                )}
                                <div className="w-4 h-4 rounded-full bg-muted text-muted-foreground text-[9px] font-semibold flex items-center justify-center">
                                  {workspace.image_url ? (
                                    <img
                                      src={workspace.image_url}
                                      alt={workspace.name}
                                      className="w-full h-full object-cover rounded-full"
                                    />
                                  ) : (
                                    getInitials(workspace.name).charAt(0)
                                  )}
                                </div>
                                <span className="text-xs">{workspace.name}</span>
                              </div>
                              {!isWorkspaceActive && (
                                <ChevronRight className="w-3 h-3 text-muted-foreground" />
                              )}
                            </DropdownMenuItem>
                          );
                        })}
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedOrgForWorkspace(org.id);
                            setCreateWorkspaceOpen(true);
                            setOpen(false);
                          }}
                          disabled={isSwitching}
                          className="pl-10"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full border border-dashed border-muted-foreground flex items-center justify-center">
                              <Plus className="w-3 h-3 text-muted-foreground" />
                            </div>
                            <span className="text-xs">Create workspace</span>
                          </div>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                  </React.Fragment>
                );
              })}

          {/* Create Workspace Button at bottom */}
          {activeOrganization && (
            <DropdownMenuItem
              onClick={() => {
                setSelectedOrgForWorkspace(activeOrganization.id);
                setCreateWorkspaceOpen(true);
                setOpen(false);
              }}
              disabled={isSwitching}
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full border border-dashed border-muted-foreground flex items-center justify-center">
                  <Plus className="w-3 h-3 text-muted-foreground" />
                </div>
                <span className="text-sm">Create workspace</span>
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create Workspace Dialog */}
      {selectedOrgForWorkspace && (
        <CreateWorkspaceForm
          organizationId={selectedOrgForWorkspace}
          isOpen={createWorkspaceOpen}
          onClose={() => {
            setCreateWorkspaceOpen(false);
            setSelectedOrgForWorkspace(null);
          }}
          onSuccess={() => {
            setCreateWorkspaceOpen(false);
            setSelectedOrgForWorkspace(null);
          }}
        />
      )}
    </>
  );
};

